import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Link2, Shield, TrendingUp, FileCheck } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { SEO } from '../components/SEO';

export const SupplierManagement = () => {
  const features = [
    {
      icon: <Link2 className="w-10 h-10 text-[#00890b]" />,
      title: 'Supplier Collaboration',
      description: 'Secure portals for supplier data exchange and collaboration',
    },
    {
      icon: <FileCheck className="w-10 h-10 text-[#00890b]" />,
      title: 'Quality Management',
      description: 'Supplier quality tracking and performance monitoring',
    },
    {
      icon: <Shield className="w-10 h-10 text-[#00890b]" />,
      title: 'Compliance Tracking',
      description: 'Ensure supplier compliance with regulations and standards',
    },
    {
      icon: <TrendingUp className="w-10 h-10 text-[#00890b]" />,
      title: 'Performance Analytics',
      description: 'Real-time supplier performance metrics and scorecards',
    },
  ];

  const capabilities = [
    'Supplier Portal Management',
    'Part Sourcing & RFQ',
    'Supplier Quality Management',
    'Compliance Documentation',
    'Supplier Scorecards',
    'Collaborative Product Development',
    'Supply Chain Visibility',
    'Manufacturer Part Management',
    'Approved Manufacturer Lists',
    'Supplier Change Management',
    'Document Exchange',
    'Performance Reporting',
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Hire Windchill SUMA Expert | Outsource Supplier Management Customization"
        description="Hire expert Windchill SUMA developers. Outsource your Supplier Management customization to experienced professionals for supply chain optimization."
        keywords="hire windchill suma expert, outsource suma customization, hire supplier management developer, windchill suma consultant, hire windchill supplier expert"
        canonical="/hire-supplier-management-expert"
      />
      <section className="bg-gradient-to-br from-white via-green-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-[#00890b] hover:bg-[#006a08] text-white px-4 py-2">
              Supplier Management (SUMA)
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Hire Windchill Supplier Management Expert
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Outsource your SUMA development to expert consultants. Hire experienced Windchill
              Supplier Management specialists for supply chain optimization
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Windchill Supplier Management (SUMA)
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            Windchill Supplier Management (SUMA) enables seamless collaboration between manufacturers
            and their supply chain partners. Our SUMA expertise helps organizations streamline
            supplier interactions, improve quality, and reduce supply chain risks.
          </p>
          <p className="text-lg text-gray-600">
            We deliver custom SUMA solutions that integrate with your existing systems, automate
            supplier workflows, and provide real-time visibility into supplier performance.
          </p>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-center mb-4">{feature.icon}</div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                  <CardDescription>{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">SUMA Capabilities</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors"
              >
                <CheckCircle className="w-6 h-6 text-[#00890b] flex-shrink-0" />
                <span className="text-gray-700 font-medium">{capability}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Professional Windchill SUMA Development
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            Our Windchill SUMA developers have extensive experience implementing supplier management
            solutions for global enterprises. We understand the complexities of multi-tier supply
            chains and deliver customizations that enhance supplier collaboration while maintaining
            security and compliance.
          </p>
          <p className="text-lg text-gray-600">
            Whether you need supplier portal customization, automated quality workflows, or advanced
            supplier analytics, our team provides end-to-end SUMA development and support services.
          </p>
        </div>
      </section>

      <ContactForm />
    </div>
  );
};